import { ArrayUtil, Currency, DateUtil, LogManager, Model, ModelRow } from "@mcleod/core";
import {
    Button, Label, Layout, LayoutLoadErrorEvent, Panel, PanelProcessLastRun, PanelProcessLastRunVariant, PanelProps,
    ReportData, SlideoutDecorator, Step, StepEvent
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutOrderPayablesProcessStepper } from "./autogen/AutogenLayoutOrderPayablesProcessStepper";
import { OrderPayablesProcessParameters } from "./OrderPayablesProcessParameters";
import { OrderPayablesProcessPostPayables } from "./OrderPayablesProcessPostPayables";
import { BackOfficeReportPreview } from "./BackOfficeReportPreview";

export interface OrderPayablesProcessStepperProps extends PanelProps {
    settlementIds: string,
    deductionIds: string,
    checkNumbers: string,
    checkNumberCount: number,
    payeeIds: string,
    payeeTypes: string,
    processCount: number,
    selectedPayables: ModelRow[],
    total: Currency,
    startDate: Date,
    endDate: Date,
    checkDate: Date,
    cutoffDate: Date,
    recurringDate: Date,
    checkNumber: string,
    profileRow: ModelRow,
    onClose: (canceled: boolean, updatedData?: ModelRow) => void,
    doAfterSlideIn: (layout: OrderPayablesProcessParameters) => void,
    doAfterSlideOut: () => void,
    payrollPosted: boolean,
    sendToImaging: boolean,
    processLog: PanelProcessLastRun,
    title: string,
    isDriverSettlement: boolean
    isOffice?: boolean,
    displayRecordSelectionDates?: boolean
    payrollCutoffDate: Date,
}

const INDEX_PARAMETERS = 0;
const INDEX_SUMMARY = 1;
const INDEX_PRINT_CHECKS = 2;
const INDEX_POST = 3;

const log = LogManager.getLogger("settlement/OrderPayablesProcessStepper");
let globalProps: Partial<OrderPayablesProcessStepperProps>;

export class OrderPayablesProcessStepper extends AutogenLayoutOrderPayablesProcessStepper {
    private payrollControl: ModelRow;
    private todaysDate: Date;
    private reportPreviewSummaries: BackOfficeReportPreview;
    private changingStep: boolean = false;

    override async onLoad() {
        this.todaysDate = DateUtil.parseDate(new Date());
        this.orderPayablesProcessStepper.components.forEach((component: Step) => component.visited = true); // give the user the ability to jump to any step
        this.layoutPayments.markStepCompleted = () => {
            this.orderPayablesPaymentsStep.checked = true;
            this.layoutPostPayables.buttonPostPayables.enabled = true;
        }
        this.layoutPostPayables.markStepCompleted = () => this.orderPayablesPostStep.checked = true;
        this.payrollControl = await Model.searchSingleRecord("lme/dispatch/payroll-control", null, ["require_pay_periods"]);
        const settlementSummaryRecord: ModelRow = await Model.searchSingleRecord("lme/general/report-property", {
            id: "Settlement Summary",
            is_active: "Y"
        });
        if (settlementSummaryRecord != null && settlementSummaryRecord.get("print_action") != "N") {
            this.layoutParameters.switchPrintSummariesToImaging.visible = true;
            this.layoutParameters.switchPrintSummariesToImaging.checked = true;
            if (settlementSummaryRecord.get("print_action") === "D" || settlementSummaryRecord.get("print_action") === "F") {
                this.layoutParameters.switchPrintSummariesToImaging.enabled = false;
            }
        }
        if (globalProps.displayRecordSelectionDates) {
            this.setupCutoffDateSection();
        }
    }

    async doBeforeStep(event: StepEvent, props: Partial<OrderPayablesProcessStepperProps>, buttonContinue: Button) {
        if (this.changingStep) {
            return;
        }
        this.doBeforeStepLayoutDisplay();

        if (event.oldIndex == INDEX_PARAMETERS) {
            props.startDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxStartDate.text, true, false);
            props.endDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxEndDate.text, true, false);
            props.checkDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxCheckDate.text, true, false);
            if (!(await this.checkProcessingYear())) {
                event.preventDefault();
                return;
            }
            this.orderPayablesParametersStep.checked = true;
            if (this.layoutParameters.switchPrintSummariesToImaging.checked && this.layoutParameters.switchPrintSummariesToImaging.visible) {
                globalProps.sendToImaging = true;
                this.layoutParameters.dataHasChanged = true;
                await this.layoutParameters.printSummaries(globalProps);
                this.orderPayablesSummaryStep.checked = true;
            }
        }

        globalProps.startDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxStartDate.text, true, false);
        globalProps.endDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxEndDate.text, true, false);
        globalProps.checkDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxCheckDate.text, true, false);

        if (INDEX_SUMMARY == event.newIndex) {
            if (this.orderPayablesSummaryStep.busy != true) {
                this.orderPayablesSummaryStep.busy = true;
                this.layoutParameters.printSummaries(globalProps)
                .then((documents: ReportData[]) => {
                    this.reportPreviewSummaries.data = documents;
                    this.orderPayablesSummaryStep.checked = true
                }).finally(() => {
                    this.orderPayablesSummaryStep.busy = false;
                });
            }
        } else if (INDEX_PRINT_CHECKS == event.newIndex) {
            await this.layoutPayments.getNextCheckNumber(globalProps);
        } else if (INDEX_POST == event.newIndex) {
            if (!this.layoutPayments.reportPreviewChecks.isEmpty()) {
                event.preventDefault();
                await CommonDialogs.showYesNo("Did all the checks print OK?", "Check Printing Confirmation").then(result => {
                    if (result === undefined) return;
                    if (result) {
                        this.changingStep = true;
                        this.orderPayablesPostStep.select();
                        this.layoutPayments.markStepCompleted?.();
                        this.layoutPostPayables.buttonPostPayables.enabled = true;
                    } else {
                        this.layoutPayments.loadPrintedChecks(globalProps);
                    }
                });
            }
            if (INDEX_POST == event.newIndex) {
                if (this.layoutPayments.printedCheckRows.length == 0){
                    await this.layoutPayments.loadPrintedChecks(globalProps, false);
                }
                this.layoutPostPayables.setToPrePostView(this.layoutPayments.printedCheckRows, props);
            }
        }

        if (event.defaultPrevented) {
            buttonContinue.visible = !this.changingStep;
        } else {
            buttonContinue.visible = event.newIndex != INDEX_POST;
        }
        this.changingStep = false;
    }

    doBeforeStepLayoutDisplay() {
        if (this.parent?.["dismissAllSnackbars"])
            this.parent["dismissAllSnackbars"]();
    }

    private async checkProcessingYear(): Promise<boolean> {
        const currentProcessingYear = this.payrollControl.get("current_process_year");
        if (currentProcessingYear != undefined && globalProps.checkDate != undefined) {
            const checkDateYear = globalProps.checkDate.getFullYear();
            if (currentProcessingYear < checkDateYear) {
                const panel = new Panel();
                panel.add(new Label({
                    text: `The selected check date is after the current processing year (${currentProcessingYear}).`,
                    fontBold: true
                }));
                panel.add(new Label({ text: `The year must be updated in Settlement Control before payments can be processed in ${checkDateYear}.` }));
                await CommonDialogs.showDialog(panel, { title: "Processing Year Error" });
                this.orderPayablesProcessStepper.selectedIndex = INDEX_PARAMETERS;
                this.layoutParameters.textboxCheckDate.focus();
                return false;
            } else if (currentProcessingYear > checkDateYear) {
                const panel = new Panel();
                panel.add(new Label({
                    text: `The selected check date is before the current processing year (${currentProcessingYear}).`,
                    fontBold: true
                }));
                panel.add(new Label({ text: `Continuing with processing for ${checkDateYear} may result in inaccurate payee year-to-date (YTD) totals.` }));
                panel.add(new Label({ text: `Would you like to proceed?` }));
                const isYes = await CommonDialogs.showYesNo(panel, "Processing Year Warning");
                if (!isYes) {
                    this.orderPayablesProcessStepper.selectedIndex = INDEX_PARAMETERS;
                    this.layoutParameters.textboxCheckDate.focus();
                    return false;
                }
            }
        }
        return true;
    }

    private setupOrderPayablesParametersLayout(props: Partial<OrderPayablesProcessStepperProps>): Promise<OrderPayablesProcessParameters> {
        return new Promise((resolve, reject) => {
            this.layoutParameters.addLayoutLoadListener(() => {
                this.layoutParameters.textboxStartDate.required = this.payrollControl.get("require_pay_periods") == "Y";

                this.layoutParameters.textboxEndDate.text = DateUtil.formatDate(DateUtil.parseDateTime(props.endDate), "MM/dd/yyyy");
                this.layoutParameters.buttonSettlementMessages.visible = props.isDriverSettlement === true;

                this.layoutParameters.textboxCheckDate.text = DateUtil.formatDate(DateUtil.parseDateTime(this.todaysDate), "MM/dd/yyyy");
                this.layoutParameters.dataHasChanged = true;

                this.layoutParameters.addLoadErrorListener((event: LayoutLoadErrorEvent) => reject(event.errorMessage));

                resolve(this.layoutParameters);
            });
        });
    }

    private initializeSummariesPanel() {
        this.reportPreviewSummaries = new BackOfficeReportPreview("Summaries", true);
        this.orderPayablesSummaryStep.add(this.reportPreviewSummaries);
    }

    private async setupOrderPayablesProcessPrintChecksLayout(props: Partial<OrderPayablesProcessStepperProps>) {
        if (props.isOffice) {
            this.layoutPayments.layoutVoidChecks.sourceDrsCheck.url = "lme/backoffice/officepayroll/off-check";
        }
        await this.layoutPayments.ensureLoaded();
        this.layoutPayments.layoutVoidChecks.selectedRows = props.selectedPayables;
        await this.layoutPayments.getNextCheckNumber(props);
        await this.layoutPayments.loadPrintedChecks(props);
        this.layoutPayments.setupVoidButton(props);
        this.layoutPayments.setupCancelVoidButton()

        this.layoutPayments.buttonGeneratePayments.addClickListener(async () => {
            props.checkNumber = this.layoutPayments.textboxCheckNumber.text;

            const numOfChecks = this.layoutPayments.getPrintedChecks().length;
            if (numOfChecks > 0 && !this.layoutPayments.layoutVoidChecks.visible) {
                const showVoid = await CommonDialogs.showYesNo("Do you wish to void any checks?", "Void Checks");
                if (showVoid === undefined) return;
                if (showVoid) {
                    this.layoutPayments.displayPrintedChecks(true);
                } else {
                    await this.layoutPayments.generateChecks(props);
                }
            } else {
                await this.layoutPayments.generateChecks(props);
            }
        });
    }

    private setupOrderPayablesProcessPostPayablesLayout(props: Partial<OrderPayablesProcessStepperProps>, buttonContinue: Button): Promise<OrderPayablesProcessPostPayables> {
        return new Promise((resolve, reject) => {
            this.layoutPostPayables.addLayoutLoadListener(() => {
                this.layoutPostPayables.addLoadErrorListener((event: LayoutLoadErrorEvent) => reject(event.errorMessage));
                buttonContinue.cancel = true;
                if (props.isOffice) {
                    this.layoutPostPayables.buttonPostPayables.tooltip = "Post Office Payroll";
                }
                else if (props.isDriverSettlement) {
                    this.layoutPostPayables.buttonPostPayables.tooltip = "Post Driver Settlement";
                }

                this.layoutPostPayables.buttonPostPayables.addClickListener(() => {
                    CommonDialogs.showYesNo("Are you sure you would like to proceed?", "Post Confirmation", { parentElement: this.layoutPostPayables._element }).then(result => {
                        if (result === true) {
                            buttonContinue.busy = true;
                            this.layoutPostPayables.buttonPostPayables.busy = true;
                            this.layoutPostPayables.reportPreviewPostPayroll.beginSearch();
                            this.layoutPostPayables.reportPreviewPostPayroll.visible = true;
                            this.layoutPostPayables.postPayroll(props)
                            .then((documents: []) => {
                                if (ArrayUtil.isEmptyArray(documents) === false) {
                                    const hasExceptions = documents.some(document => {
                                        return document["report_name"] == "Exceptions";
                                    });
                                    this.layoutPostPayables.reportPreviewPostPayroll.endSearch(documents);
                                    if (!hasExceptions) {
                                        props.payrollPosted = true;
                                        this.layoutPostPayables.setToPostedView();
                                    }
                                }
                            })
                            .finally(() => {
                                this.layoutPostPayables.buttonPostPayables.busy = false;
                                buttonContinue.busy = false;
                            });
                        } else {
                            this.layoutPostPayables.buttonPostPayables.enabled = true;
                            return;
                        }
                    });
                });

                resolve(this.layoutPostPayables);
            });
        });
    }

    advanceToNextStep() {
        this.orderPayablesProcessStepper.selectedIndex = this.orderPayablesProcessStepper.selectedIndex + 1;
    }

    private createContinueButton(): Button {
        return new Button({
            id: "buttonContinue",
            caption: "Continue",
            backgroundColor: "primary",
            color: "primary.reverse",
            rowBreak: false,
            borderWidth: 0,
            onClick: () => {
                this.advanceToNextStep()
            }
        });
    }

    static async showAsSlideout(props: Partial<OrderPayablesProcessStepperProps>) {
        globalProps = props;
        const stepperLayout = Layout.getLayout("lme/settlement/OrderPayablesProcessStepper", { ...props }) as OrderPayablesProcessStepper;
        const addlComponents = [];
        const processLog = PanelProcessLastRun.create(props.isOffice ? "Office Payroll Posting" : "Settlement Posting", {
            variant: PanelProcessLastRunVariant.compact
        });
        if (processLog) {
            props.processLog = processLog;
            addlComponents.push(processLog);
        }
        const buttonContinue = stepperLayout.createContinueButton();
        addlComponents.push(buttonContinue);

        SlideoutDecorator.decorateAndSlideIn(stepperLayout, {
            title: props.title,
            fillVerticalSpace: true,
            width: "60%",
            doAfterSlideOut: () => {
                if (props.payrollPosted) {
                    props.doAfterSlideOut?.();
                }
            },
            layoutLoadListeners: () => {
                stepperLayout.orderPayablesProcessStepper.addBeforeStepListener((event: StepEvent) => {
                    stepperLayout.doBeforeStep(event, props, buttonContinue);
                });
                stepperLayout.setupOrderPayablesParametersLayout(props)
                .then((orderPayablesProcessParameters) => {
                    stepperLayout.doBeforeStepLayoutDisplay();
                    if (props.isOffice) {
                        orderPayablesProcessParameters.labelHeaderOne.text = orderPayablesProcessParameters.labelHeaderOne.text.replace("settlement", "office pay");
                    }
                    orderPayablesProcessParameters.textboxStartDate.focus();
                    if (props.doAfterSlideIn != null) {
                        props.doAfterSlideIn(orderPayablesProcessParameters);
                    }
                    stepperLayout.initializeSummariesPanel();
                    stepperLayout.setupOrderPayablesProcessPrintChecksLayout(props).then(() => {
                        stepperLayout.setupOrderPayablesProcessPostPayablesLayout(props, buttonContinue);
                    });
                }).catch(reason => {
                    CommonDialogs.showError(reason);
                });
            },
            addlComponents: addlComponents,
            overlayProps: { closeOnClickOff: false, greyedBackground: true }
        });
    }

    private setupCutoffDateSection() {
        this.layoutParameters.panelRecordSelection.visible = true;
        const overrideCutoffDate = DateUtil.parseDateWithKeywords(globalProps.profileRow.get("cutoff_date"));
        const payrollCutoffDate = DateUtil.parseDateTime(globalProps.payrollCutoffDate);
        this.layoutParameters.textboxOverrideCutoff.text = DateUtil.formatDate(overrideCutoffDate);
        this.layoutParameters.textboxPayrollCutoff.text = DateUtil.formatDate(payrollCutoffDate);

        if (!overrideCutoffDate) {
            this.layoutParameters.labelSelectionDescr.text = "Using the Payroll Cutoff Date to select " +
                "all records: settlements, pending pay adjustments, and recurring pay " +
                "adjustments.";
            return;
        }

        if (overrideCutoffDate > payrollCutoffDate) {
            this.layoutParameters.labelSelectionDescr.text = "Using the Override Cutoff Date to select " +
                "settlement and pending pay adjustment records. Using the Payroll Cutoff " +
                "Date (earlier date) to select recurring pay adjustments.";
        } else {
            this.layoutParameters.labelSelectionDescr.text = "Using the Override Cutoff Date (earlier " +
                "date) to select all records: settlements, pending pay adjustments, and " +
                "recurring pay adjustments.";
        }
    }
}
